import { useEffect, useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { trpc } from '@/lib/trpc'
import {
  buildReviewModel,
  summarizeAuditEvidence,
} from './auditor-review-model'

/** Audits cards and overview share the complete cycle-scoped population. */
export function useAuditCycleOverview(auditCycleId: string) {
  const query = useInfiniteQuery({
    ...trpc.auditor.getControlsTree.infiniteQueryOptions({
      auditCycleId,
      status: 'all',
      reviewStatus: 'all',
      limit: 500,
    }),
    getNextPageParam: (page) => page.nextCursor,
    staleTime: 60_000,
  })
  const {
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = query
  const pageCount = query.data?.pages.length ?? 0
  useEffect(() => {
    if (
      hasNextPage &&
      !isFetchingNextPage &&
      !isFetchNextPageError &&
      pageCount < 10
    ) {
      void fetchNextPage()
    }
  }, [
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    pageCount,
    fetchNextPage,
  ])

  const model = useMemo(
    () => buildReviewModel(query.data?.pages ?? [], { includeDeferred: false }),
    [query.data]
  )
  const evidence = useMemo(
    () => summarizeAuditEvidence([...model.testsById.values()]),
    [model]
  )
  const approved = model.controls.filter(
    (control) => control.approvalStage === 'APPROVED'
  ).length
  const applicable = model.controls.filter(
    (control) => control.approvalStage !== 'NOT_APPLICABLE'
  ).length
  // A partial or failed query must not turn into a plausible percentage.
  const approvalPct =
    query.isPending || query.isError || query.hasNextPage || applicable === 0
      ? null
      : Math.round((approved / applicable) * 100)
  return { query, model, evidence, approved, applicable, approvalPct }
}
